class ForecastSaleMessage extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
    this.intervalId = null;
    this.unsubscribe = null;
    this.fromMs = 0;
    this.toMs = 0;
    this.hideSection = false;
  }

  connectedCallback() {
    if (this.initialized || !window.ForecastShopifyTime) {
      return;
    }

    this.initialized = true;
    this.fromMs = window.ForecastShopifyTime.parseUnixMs(this.dataset.from);
    this.toMs = window.ForecastShopifyTime.parseUnixMs(this.dataset.to);
    this.hideSection = this.dataset.hideSection === 'true';

    if (!this.fromMs && !this.toMs) {
      return;
    }

    window.ForecastShopifyTime.initFromLiquid(this.dataset.now);
    this.applyVisibility();
    this.intervalId = setInterval(() => this.applyVisibility(), 1000);
    this.unsubscribe = window.ForecastShopifyTime.subscribe(() => this.applyVisibility());
    window.ForecastShopifyTime.sync();
  }

  disconnectedCallback() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
  }

  get isDesignMode() {
    return Boolean(window.Shopify && Shopify.designMode);
  }

  isInWindow(now) {
    if (this.fromMs && now < this.fromMs) {
      return false;
    }

    if (this.toMs && now > this.toMs) {
      return false;
    }

    return true;
  }

  applyVisibility() {
    if (this.isDesignMode && this.dataset.keepInEditor === 'true') {
      this.setVisible(true);
      return;
    }

    this.setVisible(this.isInWindow(window.ForecastShopifyTime.now()));
  }

  setVisible(visible) {
    const targets = [this];

    if (this.hideSection) {
      const section = this.closest('.shopify-section');
      if (section) {
        targets.push(section);
      }
    }

    targets.forEach((el) => {
      if (visible) {
        el.removeAttribute('hidden');
        el.removeAttribute('aria-hidden');
        el.style.display = '';
      } else {
        el.setAttribute('hidden', '');
        el.setAttribute('aria-hidden', 'true');
        el.style.display = 'none';
      }
    });
  }
}

class ForecastSalePrice extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
    this.intervalId = null;
    this.unsubscribe = null;
    this.fromMs = 0;
    this.toMs = 0;
    this.originalPrice = 0;
    this.forecastPrice = 0;
    this.nativeCompare = 0;
    this.priceVaries = false;
    this.fromTemplate = '';
    this.savedTemplate = '';
  }

  connectedCallback() {
    if (this.initialized || !window.ForecastShopifyTime) {
      return;
    }

    this.initialized = true;
    this.priceEl = this.querySelector('.price');
    this.regularEl = this.querySelector('.price__regular .price-item--regular');
    this.compareEl = this.querySelector('.price__sale .price-item--regular');
    this.saleEl = this.querySelector('.price__sale .price-item--sale');
    this.compareWrap = this.querySelector('.price__compare');
    this.saleWrap = this.querySelector('.price__sale [data-last]');
    this.savedEl = this.querySelector('.price-item--saved');

    this.fromMs = window.ForecastShopifyTime.parseUnixMs(this.dataset.from);
    this.toMs = window.ForecastShopifyTime.parseUnixMs(this.dataset.to);
    this.originalPrice = Number(this.dataset.originalPrice) || 0;
    this.forecastPrice = Number(this.dataset.forecastPrice) || 0;
    this.nativeCompare = Number(this.dataset.nativeCompare) || 0;
    this.priceVaries = this.dataset.priceVaries === 'true';
    this.fromTemplate = this.dataset.fromTemplate || '';
    this.savedTemplate = this.dataset.savedTemplate || '';

    if (!this.priceEl || this.originalPrice <= 0) {
      return;
    }

    window.ForecastShopifyTime.initFromLiquid(this.dataset.now);
    this.applyState();
    this.intervalId = setInterval(() => this.applyState(), 1000);
    this.unsubscribe = window.ForecastShopifyTime.subscribe(() => this.applyState());
    window.ForecastShopifyTime.sync();
  }

  disconnectedCallback() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
  }

  isInWindow(now) {
    if (this.fromMs && now < this.fromMs) {
      return false;
    }

    if (this.toMs && now > this.toMs) {
      return false;
    }

    return true;
  }

  formatMoney(cents) {
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, window.money_format);
    }

    return String(cents);
  }

  formatDisplay(cents) {
    const formatted = this.formatMoney(cents);

    if (this.priceVaries && this.fromTemplate) {
      return this.fromTemplate.replace(/%%PRICE%%/g, formatted);
    }

    return formatted;
  }

  setSaved(compareCents, saleCents) {
    if (!this.savedEl) {
      return;
    }

    const savedCents = compareCents - saleCents;
    if (savedCents <= 0) {
      this.savedEl.innerHTML = '';
      return;
    }

    const savedMoney = this.formatMoney(savedCents);
    this.savedEl.innerHTML = this.savedTemplate
      ? this.savedTemplate.replace(/%%PRICE%%/g, savedMoney)
      : savedMoney;
  }

  applyState() {
    const forecastActive = this.isInWindow(window.ForecastShopifyTime.now());

    if (forecastActive) {
      this.priceEl.classList.add('price--on-sale');
      if (this.compareEl) {
        this.compareEl.innerHTML = this.formatMoney(this.originalPrice);
      }
      if (this.saleEl) {
        this.saleEl.innerHTML = this.formatDisplay(this.forecastPrice);
      }
      if (this.compareWrap) {
        this.compareWrap.setAttribute('data-compare', String(this.originalPrice));
      }
      if (this.saleWrap) {
        this.saleWrap.setAttribute('data-last', String(this.forecastPrice));
      }
      this.setSaved(this.originalPrice, this.forecastPrice);
      return;
    }

    const nativeOnSale = this.nativeCompare > this.originalPrice;

    if (this.regularEl) {
      this.regularEl.innerHTML = this.formatDisplay(this.originalPrice);
    }

    if (nativeOnSale) {
      this.priceEl.classList.add('price--on-sale');
      if (this.compareEl) {
        this.compareEl.innerHTML = this.formatMoney(this.nativeCompare);
      }
      if (this.saleEl) {
        this.saleEl.innerHTML = this.formatDisplay(this.originalPrice);
      }
      if (this.compareWrap) {
        this.compareWrap.setAttribute('data-compare', String(this.nativeCompare));
      }
      if (this.saleWrap) {
        this.saleWrap.setAttribute('data-last', String(this.originalPrice));
      }
      this.setSaved(this.nativeCompare, this.originalPrice);
      return;
    }

    this.priceEl.classList.remove('price--on-sale');
    this.setSaved(0, 0);
  }
}

if (!customElements.get('forecast-sale-message')) {
  customElements.define('forecast-sale-message', ForecastSaleMessage);
}

if (!customElements.get('forecast-sale-price')) {
  customElements.define('forecast-sale-price', ForecastSalePrice);
}
